import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import './DictionaryPanel.css';

const DICT_CACHE_KEY = 'chikhise-dictionary-cache-v2';
const SAVED_KEY = 'chikhise-vocabulary-v1';

function readJson(key, fallback) {
  try { return JSON.parse(localStorage.getItem(key) || JSON.stringify(fallback)); } catch { return fallback; }
}

function normalize(value) {
  return String(value || '').trim().replace(/\s+/g, ' ');
}

export default function DictionaryPanel({ selectedWord, apiBase, userId, bookId, bookTitle, onSaveWord, isMobileOpen, onToggleMobile }) {
  const [query, setQuery] = useState('');
  const [entry, setEntry] = useState(null);
  const [meaning, setMeaning] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  const word = normalize(query || selectedWord);
  const displayWord = word || 'یک کلمه را انتخاب کنید';

  const lookup = async (raw) => {
    const target = normalize(raw);
    if (!target) return;
    setQuery(target);
    setLoading(true);
    setError('');
    setCopied(false);
    try {
      const cache = readJson(DICT_CACHE_KEY, {});
      const cacheKey = target.toLowerCase();
      const cached = cache[cacheKey];
      if (cached && cached.entry?.found) {
        setEntry(cached.entry);
        setMeaning(cached.meaning || cached.entry.meaning || '');
        setSaved(readJson(SAVED_KEY, []).some(v => v.word?.toLowerCase() === target.toLowerCase()));
        setLoading(false);
        return;
      }

      // Try all supported backend routes. This is important when an older
      // deployment still exposes only /lookup or when a reverse proxy uses /api.
      const base = String(apiBase || window.__CHIKHISE_API_URL__ || '').replace(/\/$/, '');
      const endpoints = [...new Set([
        `${base}/lookup`,
        `${base}/dictionary/lookup`,
        `${base}/api/lookup`
      ].filter(Boolean))];

      let data = null;
      let lastError = null;
      for (const endpoint of endpoints) {
        try {
          const response = await axios.get(endpoint, {
            params: { word: target },
            timeout: 12000,
            validateStatus: status => status >= 200 && status < 500
          });
          const candidate = response.data || {};
          // A valid new dictionary response has found:boolean. If an old route
          // says "not found", continue to the other aliases before giving up.
          if (candidate.found === true) { data = candidate; break; }
          if (candidate.found === false) data = candidate;
        } catch (err) {
          lastError = err;
        }
      }

      // Last-resort local dictionary. This makes the reader independent from
      // the deployed API/reverse-proxy and guarantees that the supplied JSON
      // dictionary is actually used by the UI.
      if (!data || data.found !== true) {
        try {
          const localResponse = await fetch('/dictionary-data/index.json', { cache: 'force-cache' });
          if (localResponse.ok) {
            const local = await localResponse.json();
            const localNormalize = (value) => String(value || '')
              .normalize('NFKC').toLowerCase().trim()
              .replace(/[’‘`´]/g, "'")
              .replace(/[^a-z0-9']/g, ' ')
              .replace(/\s+/g, ' ').trim();
            const localCompact = (value) => localNormalize(value).replace(/[^a-z0-9]/g, '');
            const keys = [localNormalize(target), localCompact(target), ...localNormalize(target).split(' ').filter(Boolean)];
            let id = null;
            for (const key of keys) { if (key && local.index?.[key]) { id = local.index[key]; break; } }
            if (id && local.entries?.[id]) data = local.entries[id];
          }
        } catch (localError) {
          // Keep the original API error below if the local fallback is unavailable.
        }
      }

      if (!data || !data.found) {
        setEntry(null);
        setMeaning('');
        setError(`کلمه «${target}» در دیکشنری پیدا نشد.`);
      } else {
        setEntry(data);
        setMeaning(data.meaning || '');
      }
      if (data.found) {
        const nextCache = { ...cache, [target.toLowerCase()]: { entry: data, meaning: data.meaning || '' } };
        try { localStorage.setItem(DICT_CACHE_KEY, JSON.stringify(nextCache)); } catch {}
      }
      // Never persist a negative lookup; remove legacy negative cache entries.
      try {
        if (cache[cacheKey] && !cache[cacheKey].entry?.found) {
          delete cache[cacheKey];
          localStorage.setItem(DICT_CACHE_KEY, JSON.stringify(cache));
        }
      } catch {}
      setSaved(readJson(SAVED_KEY, []).some(v => v.word?.toLowerCase() === target.toLowerCase()));
    } catch (e) {
      setError('امکان دریافت معنی این کلمه وجود ندارد.');
      setEntry(null);
      setMeaning('');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedWord) lookup(selectedWord);
    // intentionally only react to selectedWord
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWord]);

  const pronunciation = entry?.phonetics?.find(p => p.audio_url)?.audio_url || entry?.phoneticAudio || '';
  const pronunciationRef = React.useRef(null);
  const playPronunciation = async () => {
    if (!pronunciation) return;
    try {
      const audio = pronunciationRef.current || new Audio();
      audio.src = pronunciation;
      audio.preload = 'auto';
      audio.currentTime = 0;
      await audio.play();
    } catch (e) {
      // Keep a visible fallback player if autoplay/play() is blocked by the browser.
      try {
        const fallback = document.createElement('audio');
        fallback.src = pronunciation; fallback.controls = true; fallback.autoplay = true;
        fallback.style.width = '100%'; fallback.style.marginTop = '8px';
        const host = document.querySelector('.dictionary-pronunciation-fallback');
        if (host) { host.innerHTML = ''; host.appendChild(fallback); }
      } catch {}
    }
  };

  const toggleSave = async () => {
    if (!word) return;
    if (saved) return;
    const text = meaning || entry?.meanings?.[0]?.definitions?.[0]?.definition || '';
    const context = entry?.meanings?.[0]?.definitions?.[0]?.example || '';
    try {
      if (onSaveWord) await onSaveWord(word, text, context);
      const list = readJson(SAVED_KEY, []);
      const next = [...list.filter(v => v.word?.toLowerCase() !== word.toLowerCase()), { word, meaning: text, context, bookId, bookTitle, savedAt: Date.now() }];
      localStorage.setItem(SAVED_KEY, JSON.stringify(next));
      setSaved(true);
    } catch {}
  };

  const copyMeaning = async () => {
    if (!meaning) return;
    try {
      await navigator.clipboard.writeText(`${word}\n${meaning}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {}
  };

  return (
    <>
      <button className="dictionary-mobile-toggle" type="button" onClick={onToggleMobile} aria-label="باز کردن دیکشنری">📖</button>
      <aside className={`dictionary-panel ${isMobileOpen ? 'mobile-open' : ''}`}>
        <div className="dictionary-header">
          <div><span className="dictionary-kicker">SMART DICTIONARY</span><h3>دیکشنری</h3></div>
          <button type="button" className="dictionary-collapse" onClick={onToggleMobile}>×</button>
        </div>


        <form className="dictionary-search" onSubmit={(e) => { e.preventDefault(); lookup(query); }}>
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="جستجوی کلمه..." aria-label="جستجوی کلمه" />
          <button type="submit" aria-label="جستجو">⌕</button>
        </form>

        <div className="dictionary-result">
          <div className="dictionary-pronunciation-fallback" aria-live="polite"></div>
          <div className="dictionary-word-row">
            <div><h4>{displayWord}</h4>{entry?.phonetic && <span>{entry.phonetic}</span>}</div>
            {pronunciation && <><audio ref={pronunciationRef} preload="none" className="dictionary-hidden-audio" /><button className="pronounce-btn" type="button" onClick={playPronunciation}>🔊</button></>}
          </div>

          {loading && <div className="dictionary-loading">در حال جستجو…</div>}
          {!loading && error && <div className="dictionary-error">{error}</div>}
          {!loading && !error && word && (
            <>
              <section><div className="dictionary-section-title">معنی فارسی</div><p className="fa-meaning">{meaning || 'معنی فارسی پیدا نشد.'}</p></section>
              {(entry?.meanings || []).slice(0, 8).map((m, i) => (
                <section key={`${m.partOfSpeech}-${i}`}>
                  <div className="dictionary-pos">{m.partOfSpeech || 'definition'}</div>
                  {m.meaningFa && <p className="fa-meaning">{m.meaningFa}</p>}
                  {m.definition && <ol><li>{m.definition}</li></ol>}
                </section>
              ))}
            </>
          )}
        </div>

        <div className="dictionary-actions">
          <button type="button" onClick={toggleSave} className={saved ? 'saved' : ''}>{saved ? '✓ ذخیره شد' : '＋ افزودن به واژگان'}</button>
          <button type="button" onClick={copyMeaning}>{copied ? '✓ کپی شد' : '⧉ کپی معنی'}</button>
        </div>
      </aside>
    </>
  );
}
